import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { TitleAndPubDate } from "../TitleAndPubDate";
import { ResourceDWR } from "../../../../dto/ResourceDWR";
import { ResourceView } from "../../../../dto/ResourceView";
import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { useToast } from "@tlc/ui/toast";
import {
  AvailabilitySource,
  AvailabilitySourceService,
} from "../../../../contexts/EResourceAvailabilityContext";
import { AccountHolder } from "@tlc/ls2pac/components/Account";
import { DialogWithDataProps } from "@tlc/ui/dialog/useDialog";

interface EResourceHold {
  user: unknown;
  service: string;
  titleId: string;
  format: string;
}

enum EResourceMessageToken {
  VALID = "<SuccessToken>",
  INVALID = "<HostErrorToken>",
  ALREADY_REQUESTED = "<TitleAlreadyOnHoldToken>",
  BRANCH_PICKUP_INVALID = "<HoldNotPlacedToken>",
  BRANCH_PICKUP_NO_PAGE = "<HoldNotPlacedToken>",
  BRANCH_TERMINAL_NO_PAGE = "<HoldNotPlacedToken>",
  HOLD_PLACED_COPIES_AVAILABLE = "<HoldAvailableToken>",
  HOLD_PLACED_ON_ORDER = "<SuccessToken>",
  PATRON_EXCEEDS_HOLD_LIMIT = "<MaximumRequestsReachedToken>",
  PATRON_EXPIRED = "<PatronDoesNotHaveAgencyTypeToken>",
  PATRON_HARD_BLOCKED = "<PatronDoesNotHaveAgencyTypeToken>",
  TITLE_DOES_NOT_CIRCULATE = "<HoldNotPlacedToken>",
  TITLE_HAS_NO_HOLDABLE_ITEMS = "<HoldNotPlacedToken>",
  TITLE_HOLDS_NOT_ALLOWED = "<HoldNotPlacedToken>",
  ERESOURCE_ALREADY_CHECKED_OUT_TO_YOU = "<eResourceAlreadyCheckedOutErrorMessage>",
  ERESOURCE_HOLD_PLACED_COPIES_AVAILABLE = "<placeHoldOnArrivedHoldErrorMessage>",
  ERESOURCE_INVALID_USER = "<eResourceInvalidUserMessage>",
  ERESOURCE_NO_EMAIL_ADDRESS = "<eResourceInvalidEmailMessage>",
  ERESOURCE_UNPAID_FINES = "<eResourceUnpaidFinesMessage>",
  ERESOURCE_EXPIRED_CARD = "<eResourceExpiredCardMessage>",
  ERESOURCE_OVERDUE_ITEMS = "<eResourceOverdueItemsMessage>",
  ERESOURCE_HISTORY_SYNC_ERROR = "<eResourceHistorySyncError>",
  ERESOURCE_ALL_COPIES_ON_HOLD = "<eResourceAllCopiesOnHold>",
  ERESOURCE_CHECKIN_FAILED = "<eResourceCheckInFailed>",
  ERESOURCE_CHECKIN_NOT_REQUIRED = "<eResourceCheckInNotRequired>",
  ERESOURCE_CANNOT_CHECKIN_DEVICE_SYNCED = "<eResourceCannotCheckInDeviceSynced>",
  ERESOURCE_EARLY_CHECKIN_NOT_ALLOWED = "<eResourceEarlyCheckInNotAllowed>",
}

interface EResourceHoldResponse {
  service: AvailabilitySourceService;
  itemId: string;
  success: boolean;
  holdsPosition: number;
  itemAvailable: boolean;
  checkedOut: boolean;
  alreadyOnHold: boolean;
  allCopiesOnHold: boolean;
  actionEnabled: boolean;
  invalidUser: boolean;
  message: EResourceMessageToken;
}

export interface DownloadableHoldData {
  resource: ResourceDWR | ResourceView;
  source: AvailabilitySource;
}

export const DownloadableHoldDialog = ({
  open,
  onClose,
  data: { resource, source },
}: DialogWithDataProps<DownloadableHoldData>) => {
  const { translate } = useLanguage();
  const { showToast } = useToast();

  const [loading, setLoading] = useState<boolean>(false);

  const submitHold = async () => {
    // format and user were not passed in 5.7.5
    const payload: Omit<EResourceHold, "user" | "format"> = {
      service: source.service,
      titleId: source.itemId,
    };
    setLoading(true);
    try {
      const { data } = await axios.post<EResourceHoldResponse>(
        `eResource/placeHold`,
        payload,
      );

      if (data.success) {
        showToast(translate("eResourceHoldsSuccessMessage"), "success");
        AccountHolder.fetchAccountSummaryAndPatron(true);
      } else if (data.checkedOut) {
        showToast(translate("TitleAlreadyCheckedOutToken"), "error");
      } else if (data.message) {
        const languageToken = data.message.replace(/</g, "").replace(/>/g, "");
        showToast(translate(languageToken), "error");
      } else {
        showToast(translate("eResourceHoldsErrorMessage"), "error");
      }
    } catch (e: unknown) {
      showToast(translate("eResourceHoldsErrorMessage"), "error");
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {translate("placeARequestPrefix")} {translate("request")}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <TitleAndPubDate resource={resource} />
        </Stack>
        <Typography>{translate("checkoutHoldEResourceInfoMessage")}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate("cancel")}</Button>
        <Button loading={loading} onClick={submitHold} variant="contained">
          {translate("submit")} {translate("request")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
